import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AirVent, Tv, WashingMachine, Box, Droplets, MapPin, Search } from 'lucide-react';
import { Service } from '../../types';
import { services } from '../../data/services';
import ActionButtons from '../shared/ActionButtons';
import LocationSelector from '../booking/LocationSelector';

interface ServiceCenterDetailsProps {
  service: Service;
  brandName?: string;
  location?: {
    state?: string;
    city?: string;
    area?: string;
  };
}

export default function ServiceCenterDetails({ service, brandName, location }: ServiceCenterDetailsProps) {
  const navigate = useNavigate();
  const [selectedState, setSelectedState] = useState(location?.state || '');
  const [selectedCity, setSelectedCity] = useState(location?.city || '');
  const [selectedArea, setSelectedArea] = useState(location?.area || '');

  const handleFindServiceCenter = () => {
    let path = `/brands/${brandName?.toLowerCase()}/${service.id}`;
    if (selectedState) {
      path += `/${selectedState.toLowerCase()}`;
      if (selectedCity) {
        path += `/${selectedCity.toLowerCase()}`;
        if (selectedArea) {
          path += `/${selectedArea.toLowerCase()}`;
        }
      }
    }
    navigate(path);
  };

  return (
    <div className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            {brandName ? `${brandName} ` : ''}{service.name} Service Center
          </h2>
          <p className="text-lg text-gray-600">
            Professional repair service center for all {brandName ? `${brandName} ` : ''}appliances
          </p>
        </div>

        {/* Location Selector */}
        {!location?.city && (
          <div className="max-w-3xl mx-auto mb-12">
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                <MapPin className="inline-block w-5 h-5 mr-2 text-blue-600" />
                Find Service Center Near You
              </h3>
              <LocationSelector
                state={selectedState}
                city={selectedCity}
                area={selectedArea}
                onStateChange={setSelectedState}
                onCityChange={setSelectedCity}
                onAreaChange={setSelectedArea}
              />
              <button
                onClick={handleFindServiceCenter}
                disabled={!selectedState}
                className="mt-4 w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                <Search className="h-5 w-5 mr-2" />
                Find Service Center
              </button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {services.map((service) => {
            const Icon = serviceIcons[service.id as keyof typeof serviceIcons];
            return (
              <div 
                key={service.id}
                className="bg-white rounded-xl border border-gray-200 hover:border-blue-500 p-6 transition-all duration-300 hover:shadow-lg"
              >
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 p-3 bg-blue-50 rounded-lg">
                    {Icon && <Icon className="h-6 w-6 text-blue-600" />}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {brandName ? `${brandName} ` : ''}{service.name} Service Center
                    </h3>
                    <p className="text-gray-600 mb-4">
                      Professional repair service center for all {brandName ? `${brandName} ` : ''}models
                    </p>
                    <div className="flex flex-wrap gap-2 mb-4">
                      <span className="text-sm bg-blue-50 text-blue-700 px-2 py-1 rounded">90-Day Warranty</span>
                      <span className="text-sm bg-green-50 text-green-700 px-2 py-1 rounded">Same Day Service</span>
                    </div>
                    {location?.state && (
                      <div className="mt-4 pt-4 border-t border-gray-100">
                        <p className="text-sm text-gray-600 flex items-center">
                          <MapPin className="h-4 w-4 mr-1 text-blue-600" />
                          Available in {[location.area, location.city, location.state].filter(Boolean).join(', ')}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-8 text-center text-white">
          <h3 className="text-2xl font-bold mb-4">
            Need {brandName ? `${brandName} ` : ''}Appliance Repair Service Center?
          </h3>
          <p className="text-lg opacity-90 mb-8">
            Our expert technicians are available 24/7 for emergency repairs
          </p>
          <ActionButtons 
            className="justify-center"
            location={location}
          />
        </div>
      </div>
    </div>
  );
}

// Map service IDs to their respective icons
const serviceIcons = {
  'ac-repair': AirVent,
  'tv-repair': Tv,
  'washing-machine-repair': WashingMachine,
  'refrigerator-repair': Box,
  'water-purifier-repair': Droplets
};